import React from 'react';
import { Card, Row, Col, Typography, Tag, Button, Space, Divider } from 'antd';
import {
  UserOutlined,
  PhoneOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  ShopOutlined,
  TeamOutlined,
  CreditCardOutlined,
  FileTextOutlined,
  MailOutlined,
  BankOutlined,
  LinkOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import NameByLang from '../../../Component/nameByLang';
import { storageUrl } from '../../../utils/apiBase';

const { Text, Title } = Typography;

interface CustomerInfoCardsProps {
  customer: any;
}

const CustomerInfoCards: React.FC<CustomerInfoCardsProps> = ({ customer }) => {
  const { t } = useTranslation();

  const getStatusColor = (status: string) => {
    const statusMap: { [key: string]: string } = {
      Active: 'green',
      Inactive: 'red',
      Pending: 'orange',
      نشط: 'green',
      'غير نشط': 'red',
      'في الانتظار': 'orange',
    };
    return statusMap[status] || 'default';
  };

  const getStatusText = (status: string) => {
    const statusMap: { [key: string]: string } = {
      Active: t('customers.status.active', 'Active'),
      Inactive: t('customers.status.inactive', 'Inactive'),
      Pending: t('customers.status.pending', 'Pending'),
      نشط: t('customers.status.active', 'Active'),
      'غير نشط': t('customers.status.inactive', 'Inactive'),
      'في الانتظار': t('customers.status.pending', 'Pending'),
    };
    return statusMap[status] || status || t('customers.status.active', 'Active');
  };

  const commercialUrl = storageUrl(customer.commercial_registration_path);
  const taxDocUrl = storageUrl(customer.tax_document_path);
  const addressDocUrl = storageUrl(customer.national_address_path);
  const hasRegistrationDocs = !!(commercialUrl || taxDocUrl || addressDocUrl);
  const isSelfRegistered =
    hasRegistrationDocs ||
    customer.status === 'Pending' ||
    !!customer.trade_name ||
    !!customer.qoyod_id;

  const openDoc = (url: string | null) => {
    if (url) window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <UserOutlined className="card-icon" />
              <div>
                <Text strong>{t('customers.details.managerName', 'Manager / Authorized')}</Text>
                <div>{customer.name || '-'}</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <BankOutlined className="card-icon" />
              <div>
                <Text strong>{t('customers.details.companyName', 'Company / Organization')}</Text>
                <div>{customer.organization || '-'}</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <ShopOutlined className="card-icon" />
              <div>
                <Text strong>{t('customers.details.tradeName', 'Trade name')}</Text>
                <div>{customer.trade_name || '-'}</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <IdcardOutlined className="card-icon" />
              <div>
                <Text strong>{t('customers.details.taxNumber', 'Tax Number')}</Text>
                <div>{customer.tax_number || '-'}</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <PhoneOutlined className="card-icon" />
              <div>
                <Text strong>{t('customers.details.phone', 'Phone')}</Text>
                <div>{customer.phone_number || '-'}</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <MailOutlined className="card-icon" />
              <div>
                <Text strong>{t('customers.details.email', 'Email')}</Text>
                <div>{customer.email || '-'}</div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <EnvironmentOutlined className="card-icon" />
              <div>
                <Text strong>{t('customers.details.address', 'Address')}</Text>
                <div>
                  {customer.billing_address
                    ? [
                        customer.billing_address.building_number,
                        customer.billing_address.billing_address,
                        customer.billing_address.billing_district ||
                          customer.billing_address.billing_state,
                        customer.billing_address.billing_city,
                        customer.billing_address.billing_zip,
                      ]
                        .filter(Boolean)
                        .join(' ')
                        .trim() || '-'
                    : '-'}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        <Col xs={24} sm={12} md={6}>
          <Card className="info-card">
            <div className="card-content">
              <div>
                <Text strong>{t('customers.details.status', 'Status')}</Text>
                <div>
                  <Tag color={getStatusColor(customer.status)}>
                    {getStatusText(customer.status)}
                  </Tag>
                  {customer.blocked ? (
                    <Tag color="red" style={{ marginInlineStart: 6 }}>
                      {t('customers.status.blocked', 'Blocked')}
                    </Tag>
                  ) : null}
                </div>
              </div>
            </div>
          </Card>
        </Col>

        {customer.customer_classification && (
          <>
            {customer.customer_classification.sales_representative && (
              <Col xs={24} sm={12} md={6}>
                <Card className="info-card">
                  <div className="card-content">
                    <TeamOutlined className="card-icon" />
                    <div>
                      <Text strong>
                        {t('customers.columns.salesRepresentative', 'Sales Representative')}
                      </Text>
                      <div>{customer.customer_classification.sales_representative.name || '-'}</div>
                    </div>
                  </div>
                </Card>
              </Col>
            )}

            {customer.customer_classification.payment_method && (
              <Col xs={24} sm={12} md={6}>
                <Card className="info-card">
                  <div className="card-content">
                    <CreditCardOutlined className="card-icon" />
                    <div>
                      <Text strong>{t('customers.columns.paymentMethod', 'Payment Method')}</Text>
                      <div>
                        <NameByLang
                          name_en={customer.customer_classification.payment_method.name_en}
                          name_ar={customer.customer_classification.payment_method.name_ar}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )}

            {customer.customer_classification.customer_type && (
              <Col xs={24} sm={12} md={6}>
                <Card className="info-card">
                  <div className="card-content">
                    <ShopOutlined className="card-icon" />
                    <div>
                      <Text strong>{t('customers.columns.customerType', 'Customer Type')}</Text>
                      <div>
                        <NameByLang
                          name_en={customer.customer_classification.customer_type.name_en}
                          name_ar={customer.customer_classification.customer_type.name_ar}
                        />
                      </div>
                    </div>
                  </div>
                </Card>
              </Col>
            )}
          </>
        )}
      </Row>

      {isSelfRegistered && (
        <Card style={{ marginBottom: 24 }} className="info-card">
          <Title level={5} style={{ marginTop: 0 }}>
            <FileTextOutlined style={{ marginInlineEnd: 8 }} />
            {t('customers.registration.title', 'Registration details')}
          </Title>
          <Divider style={{ margin: '12px 0' }} />

          <Row gutter={[16, 12]}>
            <Col xs={24} sm={12} md={8}>
              <Text type="secondary">{t('customers.details.vatRegistered', 'VAT registered')}</Text>
              <div>
                <Tag color={customer.tax_number ? 'blue' : 'default'}>
                  {customer.tax_number ? t('common.yes', 'Yes') : t('common.no', 'No')}
                </Tag>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text type="secondary">{t('customers.details.qoyodId', 'Qoyod ID')}</Text>
              <div>{customer.qoyod_id || '-'}</div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text type="secondary">
                {t('customers.details.qoyodSyncedAt', 'Qoyod synced at')}
              </Text>
              <div>
                {customer.qoyod_synced_at
                  ? new Date(customer.qoyod_synced_at).toLocaleString()
                  : '-'}
              </div>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Text type="secondary">{t('customers.details.createdAt', 'Registered at')}</Text>
              <div>
                {customer.created_at ? new Date(customer.created_at).toLocaleString() : '-'}
              </div>
            </Col>
          </Row>

          {hasRegistrationDocs && (
            <>
              <Divider style={{ margin: '16px 0 12px' }} />
              <Text strong style={{ display: 'block', marginBottom: 12 }}>
                {t('customers.registration.attachments', 'Attachments')}
              </Text>
              <Space wrap>
                <Button
                  icon={<LinkOutlined />}
                  disabled={!commercialUrl}
                  onClick={() => openDoc(commercialUrl)}
                >
                  {t('customers.registration.commercial', 'Commercial registration')}
                </Button>
                <Button
                  icon={<LinkOutlined />}
                  disabled={!taxDocUrl}
                  onClick={() => openDoc(taxDocUrl)}
                >
                  {t('customers.registration.taxDocument', 'Tax document')}
                </Button>
                <Button
                  icon={<LinkOutlined />}
                  disabled={!addressDocUrl}
                  onClick={() => openDoc(addressDocUrl)}
                >
                  {t('customers.registration.nationalAddress', 'National address')}
                </Button>
              </Space>
            </>
          )}
        </Card>
      )}
    </>
  );
};

export default CustomerInfoCards;
